<script setup>
import MonthYearSelector from "@/components/DatePicker/MonthYearSelector.vue";
import DateGrid from "@/components/DatePicker/DateGrid.vue";
import DateView from "@/components/DatePicker/DateView.vue";
import DateControl from "@/components/DatePicker/DateControl.vue";
import {computed, defineEmits, onMounted, ref, defineProps} from "vue";

const props = defineProps({
  /**
   * Тип активного календаря. Одиночный или рейнж
   */
  activeCalendarType: {
    type: String,
    required: true,
    default: "range"
  },
  /** Использование подтверждения и отображения активной даты  */
  useConfirmation: {
    type: Boolean,
    default: false
  },
  position: {
    type: Object,
    default: () => ({ top: 0, left: 0 })
  },
  /** Для диапазона: [start, end]; для single: Date */
  modelValue: {
    type: [Array, Date, null],
    required: true
  },
  /** Файл локализации */
  localeMessages: {
    type: Object,
    required: true
  },
  /** До какого дня разрешено выбирать дату */
  maxDate: {
    type: [Date, null],
    default: null
  }
});

const emit = defineEmits(["hide-date-picker", "update:modelValue"]);

onMounted(() => {
  if (props.modelValue) {
    selectedDate.value = props.modelValue;

    // Если есть выбранная дату, то календарь открывается на этой дате.
    if (Array.isArray(props.modelValue)) {
      if (props.modelValue[0]) activeDate.value = props.modelValue[0];
    } else if (Object.prototype.toString.call(props.modelValue) === "[object Date]") {
      activeDate.value = props.modelValue;
    }
  }
});

// Активная дата отображаемая в календаре
const activeDate = ref(new Date());

// Год и месяц активной даты
const currentYear  = computed(() => activeDate.value.getFullYear());
const currentMonth = computed(() => activeDate.value.getMonth());

// переключение месяцев вперед, но не дальше текущего
const switchToNextMonth = () => {
  const y = activeDate.value.getFullYear();
  const m = activeDate.value.getMonth();
  activeDate.value = new Date(y, m + 1, 1);
};

// переключение месяцев назад
const switchToPreviousMonth = () => {
  const y = activeDate.value.getFullYear();
  const m = activeDate.value.getMonth();
  activeDate.value = new Date(y, m - 1, 1);
};

// Переключение на произвольный месяц
const switchMonth = (monthIndex) => {
  const y = activeDate.value.getFullYear();
  activeDate.value = new Date(y, monthIndex, 1);
};

// Переключение на произвольный год
const switchYear = (year) => {
  const m = activeDate.value.getMonth();
  activeDate.value = new Date(year, m, 1);
};

// Выбранные даты
const selectedDate = ref([null, null]);

// Выбор даты
const selectDate = (date) => {
  if (props.activeCalendarType === "range") {
    const [start, end] = selectedDate.value;

    // Если ещё нет ни одной даты — назначаем первую
    if (!start) {
      selectedDate.value = [date, null];
      return;
    }

    // Если есть только start — выбираем вторую
    if (start && !end) {
      if (date < start) {
        // если вторая меньше первой — меняем их местами
        selectedDate.value = [date, start];
      } else {
        selectedDate.value = [start, date];
      }
      if (!props.useConfirmation) updateModelValue();
      return;
    }
    // Если уже обе даты заданы — сбрасываем и начинаем заново
    selectedDate.value = [date, null];

  } else {
    // одиночный выбор
    selectedDate.value = date;
  }

  if (!props.useConfirmation) updateModelValue();
};

// Обновление даты в родителе, в зависимости от типа календаря.
// Начальная дата всегда выставляеятся на 00:00
// Конечная дата всегда выставляется на 23:59
const updateModelValue = () => {
  const value = selectedDate.value;
  let toEmit;

  if (Array.isArray(value)) {
    // Диапазон [start, end]
    const [start, end] = value;
    const normalizedStart = start
        ? new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0)
        : null;
    const normalizedEnd = end
        ? new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 0, 0)
        : null;
    toEmit = [normalizedStart, normalizedEnd];
  } else if (value instanceof Date) {
    // Одиночная дата
    toEmit = new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        0, 0, 0, 0
    );
  } else {
    // null или что-то ещё нестандартное
    toEmit = null;
  }

  emit("update:modelValue", toEmit);
  if (props.useConfirmation) {
    emit("hide-date-picker");
  }
};
</script>

<template>
  <div
    class="date-picker"
    :style="{
      position: 'absolute',
      top: props.position.top + 'px',
      left: props.position.left + 'px'
    }"
  >
    <MonthYearSelector
      :month="currentMonth"
      :year="currentYear"
      :locale-messages="props.localeMessages"
      :max-date="props.maxDate"
      @switch-year="year => switchYear(year)"
      @switch-month="monthIndex => switchMonth(monthIndex)"
      @switch-to-previous-month="switchToPreviousMonth"
      @switch-to-next-month="switchToNextMonth"
    />
    <DateGrid
      :month="currentMonth"
      :year="currentYear"
      :locale-messages="props.localeMessages"
      :selected-date="selectedDate"
      :max-date="props.maxDate"
      @select-date="date => selectDate(date)"
    />
    <template v-if="useConfirmation">
      <DateView :selected-date="selectedDate" />
      <DateControl
        :locale-messages="props.localeMessages"
        @update-date="updateModelValue"
        @hide-date-picker="emit('hide-date-picker')"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>

.date-picker {
  max-width: 250px;
  background: #FFFFFF;
  box-shadow: 1px 2px 10px 0 #131E2726;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  z-index: 9999;
}

</style>